import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../../api';
import PasswordProtection from '../../components/PasswordProtection';

const VaultUserSignupList = () => {
  const [signups, setSignups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(500);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedSignup, setSelectedSignup] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [showOnlyEmailChanged, setShowOnlyEmailChanged] = useState(false);
  const [acknowledgingChanges, setAcknowledgingChanges] = useState(false);

  // Debounced search
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1); // Reset to first page when search changes
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Calculate skip from currentPage
  const skip = (currentPage - 1) * limit;

  // Fetch signups with server-side search and pagination
  const fetchSignups = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {
        skip,
        limit,
        ...(debouncedSearchTerm && { search: debouncedSearchTerm }),
      };
      const response = await api.get('/users-signup', { params });
      setSignups(response.data);
      // Get total count from response header (axios normalizes headers to lowercase)
      const totalCountHeader = response.headers['x-total-count'] || response.headers['X-Total-Count'] || response.headers['X-TOTAL-COUNT'];
      if (totalCountHeader) {
        const count = parseInt(totalCountHeader, 10);
        if (!isNaN(count) && count > 0) {
          setTotalCount(count);
        }
      } else {
        // Fallback: estimate based on response length if header not available
        if (response.data.length < limit) {
          setTotalCount(skip + response.data.length);
        } else if (totalCount === 0 || skip + limit + 1 > totalCount) {
          // Only update if we don't have a count yet or if this estimate is higher
          setTotalCount(skip + limit + 1);
        }
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to fetch signups');
      setSignups([]);
    } finally {
      setLoading(false);
    }
  }, [skip, limit, debouncedSearchTerm]);

  useEffect(() => {
    fetchSignups();
  }, [fetchSignups]);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  // Format datetime
  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  // Format address
  const formatAddress = (signup) => {
    const parts = [];
    if (signup.street_address_1) parts.push(signup.street_address_1);
    if (signup.street_address_2) parts.push(signup.street_address_2);
    if (signup.street_address_3) parts.push(signup.street_address_3);
    
    const cityParts = [];
    if (signup.city) cityParts.push(signup.city);
    if (signup.region) cityParts.push(signup.region);
    if (signup.zip_code) cityParts.push(signup.zip_code);
    if (cityParts.length > 0) parts.push(cityParts.join(', '));
    
    if (signup.country) parts.push(signup.country);
    
    return parts.length > 0 ? parts.join(', ') : '-';
  };

  // Export to CSV
  const handleExportAll = () => {
    const headers = [
      'ID', 'User Code', 'Email', 'First Name', 'Last Name', 'Phone Number',
      'Date of Birth', 'Street Address 1', 'Street Address 2', 'Street Address 3',
      'City', 'Region', 'Zip Code', 'Country', 'Source', 'Created At', 'Updated At'
    ];
    const csvRows = [headers.join(',')];

    signups.forEach((signup) => {
      const row = [
        signup.id || '',
        signup.user_code || '',
        signup.email || '',
        signup.first_name || '',
        signup.last_name || '',
        signup.phone_number || '',
        signup.date_of_birth || '',
        signup.street_address_1 || '',
        signup.street_address_2 || '',
        signup.street_address_3 || '',
        signup.city || '',
        signup.region || '',
        signup.zip_code || '',
        signup.country || '',
        signup.type || 'import',
        signup.created_at || '',
        signup.updated_at || '',
      ];
      csvRows.push(row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `vault-signups-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Handle view details
  const handleView = (signup) => {
    setSelectedSignup(signup);
    setShowModal(true);
  };

  // Handle delete
  const handleDelete = async (signupId) => {
    if (!window.confirm('Are you sure you want to delete this signup email?')) {
      return;
    }

    try {
      setDeletingId(signupId);
      await api.delete(`/users-signup/${signupId}`);
      await fetchSignups(); // Refresh the list
    } catch (err) {
      alert(err.response?.data?.detail || err.message || 'Failed to delete signup email');
    } finally {
      setDeletingId(null);
    }
  };

  const emailChangedRecords = useMemo(() => {
    if (loading) return [];
    return (signups || []).filter((s) => Boolean(s?.is_email_changed) && !Boolean(s?.is_acknowledged));
  }, [loading, signups]);

  const showEmailChangeBanner = emailChangedRecords.length > 0;

  const visibleSignups = showOnlyEmailChanged ? emailChangedRecords : signups;

  return (
    <PasswordProtection
      password="Signup@123!123"
      storageKey="vault_signup_authenticated"
      title="Password Required"
      description="Please enter the password to access Vault Signup Emails."
    >
    <div className="container-fluid px-3 py-2" style={{ fontSize: '13px' }}>
      {/* Heading and Search bar */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <h4 className="mb-0" style={{ fontSize: '18px', fontWeight: '600' }}>
          Vault Signup Emails
        </h4>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <div className="input-group" style={{ maxWidth: '400px' }}>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Search by user code, name, email, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                className="btn btn-outline-secondary btn-sm"
                type="button"
                onClick={() => setSearchTerm('')}
              >
                ✕
              </button>
            )}
          </div>
          <button
            className="btn btn-primary btn-sm"
            onClick={handleExportAll}
            disabled={loading || signups.length === 0}
          >
            Export CSV
          </button>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="alert alert-danger alert-dismissible fade show py-2" role="alert">
          <strong>Error:</strong> {error}
          <button
            type="button"
            className="btn-close btn-close-sm"
            onClick={() => setError(null)}
            aria-label="Close"
          ></button>
        </div>
      )}

      {showEmailChangeBanner && (
        <div
          className="alert alert-danger border-0 shadow-sm py-2 mb-2"
          role="alert"
          style={{
            background: 'linear-gradient(90deg, rgba(220,53,69,0.18) 0%, rgba(220,53,69,0.06) 70%, rgba(255,255,255,0) 100%)',
          }}
        >
          <div className="d-flex align-items-start justify-content-between gap-2 flex-wrap">
            <div>
              <div className="d-flex align-items-center gap-2">
                <span className="badge bg-danger" style={{ fontSize: '11px' }}>
                  Signup email changed
                </span>
                <strong style={{ fontSize: '13px' }}>
                  {emailChangedRecords.length} record{emailChangedRecords.length === 1 ? '' : 's'}
                </strong>
              </div>
              <div className="text-muted" style={{ fontSize: '12px', marginTop: '2px' }}>
                One or more signup emails were changed, replaced, or added. Review them, then acknowledge to hide this notice.
              </div>
            </div>
            <div className="d-flex align-items-center gap-2">
              <button
                type="button"
                className={`btn btn-sm ${showOnlyEmailChanged ? 'btn-dark' : 'btn-outline-dark'}`}
                onClick={() => setShowOnlyEmailChanged((v) => !v)}
                style={{ fontSize: '12px' }}
              >
                {showOnlyEmailChanged ? 'Showing changed only' : 'Show changed only'}
              </button>
              <button
                type="button"
                className="btn btn-sm btn-danger"
                onClick={async () => {
                  try {
                    setAcknowledgingChanges(true);
                    const changeLogIds = emailChangedRecords
                      .map((s) => s?.change_log_id)
                      .filter((v) => Number.isInteger(v));

                    if (changeLogIds.length > 0) {
                      await api.post('/users-signup/acknowledge-email-changes', {
                        change_log_ids: changeLogIds,
                      });
                      await fetchSignups();
                    }

                  } catch (err) {
                    setError(err.response?.data?.detail || err.message || 'Failed to acknowledge email changes');
                  } finally {
                    setAcknowledgingChanges(false);
                  }
                }}
                disabled={acknowledgingChanges}
                style={{ fontSize: '12px' }}
              >
                {acknowledgingChanges ? 'Acknowledging...' : 'Acknowledge'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="table-responsive" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        <table className="table table-sm table-striped table-bordered table-hover mb-0">
          <thead className="table-light" style={{ position: 'sticky', top: 0, zIndex: 10 }}>
            <tr>
              <th style={{ minWidth: '100px' }}>User Code</th>
              <th style={{ minWidth: '200px' }}>Email</th>
              <th style={{ minWidth: '120px' }}>Name</th>
              <th style={{ minWidth: '130px' }}>Phone</th>
              <th style={{ minWidth: '100px' }}>DOB</th>
              <th style={{ minWidth: '200px' }}>Address</th>
              <th style={{ minWidth: '100px' }}>Source</th>
              <th style={{ minWidth: '150px' }}>Created</th>
              <th style={{ minWidth: '120px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="9" className="text-center text-muted py-4">
                  <div className="spinner-border spinner-border-sm me-2" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  Loading signup emails...
                </td>
              </tr>
            ) : visibleSignups.length === 0 ? (
              <tr>
                <td colSpan="9" className="text-center text-muted py-4">
                  {debouncedSearchTerm ? 'No signup emails found matching your search' : 'No signup emails found'}
                </td>
              </tr>
            ) : (
              visibleSignups.map((signup, index) => (
                <tr key={signup.id || `vault-${signup.email}-${index}`}>
                  <td><strong className="text-primary">{signup.user_code || '-'}</strong></td>
                  <td>
                    {!signup?.is_acknowledged && signup?.is_email_changed && signup?.previous_email && signup?.previous_email !== signup?.email ? (
                      <div style={{ lineHeight: 1.15 }}>
                        <div>
                          <span
                            style={{
                              color: '#dc3545',
                              textDecoration: 'line-through',
                              fontSize: '12px',
                            }}
                            title="Previous signup email"
                          >
                            {signup.previous_email}
                          </span>
                        </div>
                        <div>
                          <a
                            href={`mailto:${signup.email}`}
                            className="text-decoration-none"
                            style={{ fontSize: '15px', fontWeight: 700 }}
                            title="Replaced signup email"
                          >
                            {signup.email || '-'}
                          </a>
                        </div>
                      </div>
                    ) : !signup?.is_acknowledged && signup?.is_email_changed && !signup?.replaced_by ? (
                      <a
                        href={`mailto:${signup.email}`}
                        className="text-decoration-none"
                        style={{ color: '#198754', fontWeight: 700 }}
                        title="New signup email added"
                      >
                        {signup.email || '-'}
                      </a>
                    ) : (
                      <a href={`mailto:${signup.email}`} className="text-decoration-none">
                        {signup.email || '-'}
                      </a>
                    )}
                  </td>
                  <td>
                    {signup.first_name || '-'} {signup.last_name || ''}
                  </td>
                  <td>
                    {signup.phone_number ? (
                      <a href={`tel:${signup.phone_number}`} className="text-decoration-none">
                        {signup.phone_number}
                      </a>
                    ) : '-'}
                  </td>
                  <td>{formatDate(signup.date_of_birth)}</td>
                  <td style={{ whiteSpace: 'pre-line', fontSize: '12px' }}>
                    {formatAddress(signup)}
                  </td>
                  <td>
                    <span 
                      className={`badge ${signup.type === 'vault' ? 'bg-success' : 'bg-info'}`}
                      style={{ fontSize: '11px' }}
                    >
                      {signup.type || 'import'}
                    </span>
                  </td>
                  <td style={{ fontSize: '12px' }}>{formatDateTime(signup.created_at)}</td>
                  <td>
                    <div className="d-flex gap-1 flex-wrap">
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => handleView(signup)}
                        title="View Details"
                        style={{ fontSize: '11px', padding: '2px 8px' }}
                      >
                        View
                      </button>
                      {signup.id && (
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => handleDelete(signup.id)}
                          disabled={deletingId === signup.id}
                          title="Delete"
                          style={{ fontSize: '11px', padding: '2px 8px' }}
                        >
                          {deletingId === signup.id ? (
                            <span className="spinner-border spinner-border-sm" role="status">
                              <span className="visually-hidden">Deleting...</span>
                            </span>
                          ) : (
                            'Delete'
                          )}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination footer */}
      {!loading && signups.length > 0 && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <div className="text-muted" style={{ fontSize: '12px' }}>
            Showing {skip + 1} to {skip + signups.length} of {totalCount} entries
          </div>
          <nav>
            <ul className="pagination pagination-sm mb-0">
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button 
                  className="page-link" 
                  onClick={() => setCurrentPage(1)} 
                  disabled={currentPage === 1}
                >
                  First
                </button>
              </li>
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => setCurrentPage((c) => Math.max(c - 1, 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
              </li>
              {(() => {
                // Calculate total pages - if we got a full page, there might be more
                const hasMore = signups.length === limit;
                const knownPages = Math.ceil(totalCount / limit);
                const totalPages = hasMore ? Math.max(knownPages, currentPage + 1) : knownPages;
                
                const maxPagesToShow = 10;
                let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
                let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
                
                if (endPage - startPage < maxPagesToShow - 1) {
                  startPage = Math.max(1, endPage - maxPagesToShow + 1);
                }
                
                const pages = [];
                for (let i = startPage; i <= endPage; i++) {
                  pages.push(i);
                }
                
                return pages.map((pageNumber) => (
                  <li
                    key={pageNumber}
                    className={`page-item ${currentPage === pageNumber ? 'active' : ''}`}
                  >
                    <button className="page-link" onClick={() => setCurrentPage(pageNumber)}>
                      {pageNumber}
                    </button>
                  </li>
                ));
              })()}
              <li
                className={`page-item ${
                  signups.length < limit || currentPage >= Math.ceil(totalCount / limit)
                    ? 'disabled'
                    : ''
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => setCurrentPage((c) => c + 1)}
                  disabled={signups.length < limit || currentPage >= Math.ceil(totalCount / limit)}
                >
                  Next
                </button>
              </li>
              <li
                className={`page-item ${
                  signups.length < limit || currentPage >= Math.ceil(totalCount / limit)
                    ? 'disabled'
                    : ''
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => {
                    const totalPages = Math.ceil(totalCount / limit) || 1;
                    setCurrentPage(totalPages);
                  }}
                  disabled={signups.length < limit || currentPage >= Math.ceil(totalCount / limit)}
                >
                  Last
                </button>
              </li>
            </ul>
          </nav>
        </div>
      )}

      {/* View Details Modal */}
      {showModal && selectedSignup && (
        <div
          className="modal fade show"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Signup Email Details</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowModal(false);
                    setSelectedSignup(null);
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row mb-3">
                  <div className="col-md-6">
                    <strong>User Code:</strong>
                    <div className="text-muted">{selectedSignup.user_code || '-'}</div>
                  </div>
                  <div className="col-md-6">
                    <strong>Buyer ID:</strong>
                    <div className="text-muted">{selectedSignup.buyer_id || '-'}</div>
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <strong>First Name:</strong>
                    <div className="text-muted">{selectedSignup.first_name || '-'}</div>
                  </div>
                  <div className="col-md-6">
                    <strong>Last Name:</strong>
                    <div className="text-muted">{selectedSignup.last_name || '-'}</div>
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <strong>Email:</strong>
                    <div>
                      <a href={`mailto:${selectedSignup.email}`} className="text-decoration-none">
                        {selectedSignup.email || '-'}
                      </a>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <strong>Phone Number:</strong>
                    <div>
                      {selectedSignup.phone_number ? (
                        <a href={`tel:${selectedSignup.phone_number}`} className="text-decoration-none">
                          {selectedSignup.phone_number}
                        </a>
                      ) : '-'}
                    </div>
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <strong>Date of Birth:</strong>
                    <div className="text-muted">{formatDate(selectedSignup.date_of_birth)}</div>
                  </div>
                  <div className="col-md-6">
                    <strong>Source:</strong>
                    <div>
                      <span 
                        className={`badge ${selectedSignup.type === 'vault' ? 'bg-success' : 'bg-info'}`}
                        style={{ fontSize: '12px' }}
                      >
                        {selectedSignup.type || 'import'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="mb-3">
                  <strong>Address:</strong>
                  <div className="text-muted" style={{ whiteSpace: 'pre-line' }}>
                    {formatAddress(selectedSignup)}
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <strong>Created At:</strong>
                    <div className="text-muted">{formatDateTime(selectedSignup.created_at)}</div>
                  </div>
                  <div className="col-md-6">
                    <strong>Updated At:</strong>
                    <div className="text-muted">{formatDateTime(selectedSignup.updated_at)}</div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowModal(false);
                    setSelectedSignup(null);
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </PasswordProtection>
  );
};

export default VaultUserSignupList;
